import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as FraudcheckActions from './fraudcheck.actions';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, withLatestFrom } from 'rxjs/operators';
import { combineLatest, noop, of } from 'rxjs';
import { globalFailure } from './../../../app/@store/common.actions';
import { getContractListConfig, getContractsMeta } from './../../../app/@store/fraudcheck/fraudcheck.selectors';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { loginSuccess } from '../auth/auth.actions';
import { ContractManagementApiService } from './../../../app/@shared/services/contractmanagementapi.service';
import { deleteAccountSuccess, patchAccountSuccess } from '../account/account.actions';
import { IApiResultV2 } from './../../../app/@store/request-options.v2.interface';
import { FraudcheckModel } from './../../../app/@shared/models/fraudcheck.model';
import * as _ from 'lodash';
import { HttpParams } from '@angular/common/http';
import { hasBillingModule } from '../partner/partner.selectors';


@Injectable()
export class FraudcheckEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly api: PublicPartnerApiService,
    private readonly contractManagementApi: ContractManagementApiService
  ) { }

  loadContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FraudcheckActions.loadFraudcheckContracts, patchAccountSuccess, deleteAccountSuccess),
      filter(action => action.fromBatch !== true),
      withLatestFrom(this.store.select(getContractListConfig), this.store.select(hasBillingModule)),
      concatMap(
        ([action, config, hasBillingModule]) => {
          if (!hasBillingModule) {
            return of();
          }

          return this.contractManagementApi.get<IApiResultV2<FraudcheckModel>>('/management/v2/recently-created-contracts').pipe(
            map((result: IApiResultV2<FraudcheckModel>) => {
              return FraudcheckActions.loadFraudcheckContractsSuccess({
                result
              });
            }),
            catchError(error => of(FraudcheckActions.activateFailure({ account_uuid: 'action.result.data[0].contract_uuid' }), globalFailure({ error })))
          )
        }
      )
    )
  );

  loadContractsTotal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FraudcheckActions.loadContractsTotal, loginSuccess, FraudcheckActions.activateSuccess, patchAccountSuccess, deleteAccountSuccess),
      withLatestFrom(this.store.select(hasBillingModule)),
      concatMap(([action, hasBillingModule]) => {
        if (!hasBillingModule) {
          return of();
        }

        return this.contractManagementApi.get<IApiResultV2<FraudcheckModel>>('/management/v2/recently-created-contracts').pipe(
          map((result: IApiResultV2<FraudcheckModel>) => {
            const total = result?.meta?.total || 0;
            return FraudcheckActions.loadContractsTotalSuccess({
              total
            });
          }),
          catchError(error => of(FraudcheckActions.loadContractsTotalFailure({ error }), globalFailure({ error, whitelist: [400, 401] })))
        );
      })
    )
  );

  loadFraucheckNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FraudcheckActions.loadFraudcheckNext),
      withLatestFrom(this.store.select(hasBillingModule), combineLatest([this.store.select(getContractsMeta), this.store.select(getContractListConfig)])),
      concatMap(([action, hasBillingModule, [_meta, _listConfig]]) => {
        if (!hasBillingModule) {
          return of();
        }

        const nextPage = _.get(_meta, 'page_number', 0) + 1;
        const totalPages = Math.ceil(_.get(_meta, 'total', 0) / _.get(_meta, 'limit', 10));

        const config = { ..._listConfig };

        if (nextPage <= totalPages) {
          config.page_number = nextPage;
        }

        return this.contractManagementApi.get<IApiResultV2<FraudcheckModel>>('/management/v2/recently-created-contracts', new HttpParams({ fromObject: { ...config } })).pipe(
          map((result: IApiResultV2<FraudcheckModel>) => {
            return FraudcheckActions.loadFraudcheckNextSuccess({
              result
            });
          }),
          catchError(error => of(FraudcheckActions.loadFraudcheckNextFailure({ error }), globalFailure({ error })))
        );

      })
    )
  );


  activate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FraudcheckActions.activate),
      concatMap(
        action =>
          this.contractManagementApi.patch<void>('/management/v2/contracts/' + action.account_uuid + '/activate').pipe(
            map(() => {
              return FraudcheckActions.activateSuccess({
                account_uuid: action.account_uuid
              });
            }),
            catchError(error => of(FraudcheckActions.activateFailure({ account_uuid: action.account_uuid }), globalFailure({ error })))
          )
      )
    )
  );

}
